import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AddTextBottom,
  AddTextTop,
  ColorButtonsWrapper,
  CountLength,
  ErrorsContainer,
  Left,
  LeftContent,
  LocalPickerWrapper,
  SettingsLabel
} from "../styles";
import { CommonInput, FlexBox, INPUT_TYPES, useBreakpoint, useIsMobile } from "@brg/ui";
import { ProjectEditPageGridLabel } from "shared/pages/projectEdit/styles";
import { ProjectEditPageGridInput } from "_deziiign/books/pages/BookEdit/styles";
import { Input } from "@brg/input";
import { LayoutConst } from "core";
import { useTranslation } from "react-i18next";
import { ProjectAuthorsChangeCbT } from "shared/pages/projectEdit/ProjectAuthorsEdit";
import { AuthorT } from "shared/pages/projectEdit/types";
import ColorPicker from "../../ColorPicker";
import { BilingWrapper, ErrorItem, RequiredErrorMsg } from "shared/pages/projectEdit/styles";
import { setProjectTitleByLang } from "shared/pages/projectEdit/helpers";
import { AppTester } from "supporting";
import { Checkbox, DropdownWrapper } from "../PresentatitonBlock";
import { LabelRedStar } from "shared/pages/designerEditView/styles";
import { useTheme } from "styled-components";
import { IAddProjectCoverContent } from "../types/AdditionalContentType";
import { Lang } from "@brg/api";
import ProjectTitleInput from "shared/pages/projectEdit/fields/ProjectTitleInput";

const BG_COLORS = [
  { label: 'black', value: '#000000' },
  { label: 'white', value: '#fff' }
]

interface ICoverProps {
  vTYPE: string;
  errorsArray?: string[];
  coAuthors?: AuthorT[];
  titleIsHidden?: boolean;
  projectInfoIsHidden?: boolean;
  additionalContent: IAddProjectCoverContent;
  errorProjectFill?: boolean;
  isBilingual?: boolean;
  longReadLength?: number;
  toggleHideTitle?: (e: any) => void;
  toggleHideInfo?: (e: any) => void;
  onAdditionalContentChange?: (data: any) => void;
  onTitlePositionChange?: (pos: string) => void;
  titlePosition?: string;
  onCoauthorChange?: (data: ProjectAuthorsChangeCbT) => void;
}

export const CoverSettingsBlock: FC<ICoverProps> = ({
  vTYPE,
  errorsArray = [],
  additionalContent,
  projectInfoIsHidden,
  isBilingual,
  errorProjectFill,
  longReadLength,
  toggleHideInfo,
  onAdditionalContentChange,
  onTitlePositionChange,
  titlePosition,
}) => {
  const { t, i18n: { language } } = useTranslation();
  const isMobile = useIsMobile();
  const is743 = useBreakpoint(LayoutConst.screen.s - 1);
  const isGeograffee = AppTester.isGeograffee();
  const isProDeziiign = AppTester.isProDeziiign();
  const theme = useTheme();
  const inputRef = useRef(null);
  const [isAutoFocus, setIsAutoFocus] = useState(false);
  const [key, setKey] = useState(1);
  const [postTextPos, setPostTextPos] = useState(titlePosition || '')


  const { title, subtitle, button, anchors } = useMemo(
    () => (additionalContent), [additionalContent])

  const translatedBgColors = useMemo(
    () => (BG_COLORS?.map(
      (i) => ({ ...i, label: t(`project_edit.settings_block.labels.${i.label}`) })))
    , [t])

  const defaultBgColor = useMemo(
    () => (translatedBgColors?.find((i) => i?.value === button?.color))
    , [button?.color, translatedBgColors])


  const onTitleChange = useCallback((ev: any) => {
    const data = {
      title: {
        ...title, text:
          setProjectTitleByLang(
            isBilingual || false,
            title.text,
            language,
            ev?.target?.value
          )
      },
      subtitle,
      button,
      anchors
    };

    onAdditionalContentChange && onAdditionalContentChange(data)
  }, [title, subtitle, button, language, anchors, onAdditionalContentChange, isBilingual])

  const onSubtitleChange = useCallback((ev: any) => {
    const data = {
      title,
      subtitle: {
        ...subtitle, text:
          setProjectTitleByLang(
            isBilingual || false,
            subtitle.text,
            language,
            ev?.target?.value
          )
      },
      button,
      anchors
    };

    onAdditionalContentChange && onAdditionalContentChange(data)
  }, [subtitle, title, button, language, isBilingual, onAdditionalContentChange])

  const onButtonChange = useCallback((ev: any, field: string) => {
    const isHref = field === 'href';

    const data = {
      title,
      subtitle,
      button: {
        ...button, [field]: isHref ? ev?.target?.value :
          setProjectTitleByLang(
            isBilingual || false,
            // @ts-ignore
            button?.[field],
            language,
            ev?.target?.value
          )
      },
      anchors
    };

    onAdditionalContentChange && onAdditionalContentChange(data)
  }, [button, title, subtitle, onAdditionalContentChange, language, isBilingual])

  const onToggleButtonVisible = useCallback((field: string) => {
    const data = { title, subtitle, button };

    if (['title', 'subtitle']?.includes(field)) {
      onAdditionalContentChange && onAdditionalContentChange({
        title: { ...title, visible: !title?.visible },
        subtitle: { ...subtitle, visible: !subtitle?.visible },
        button,
        anchors
      })
      return;
    }

    onAdditionalContentChange && onAdditionalContentChange({
      ...data,
      [field]: {
        // @ts-ignore
        ...data[field as keyof IAddProjectCoverContent]
        // @ts-ignore
        , visible: !data[field as keyof IAddProjectCoverContent].visible
      }
    })
  }, [title, button, subtitle, anchors, onAdditionalContentChange])

  const onLocalColorChange = useCallback((color: string, k: any) => {
    const data = { title, subtitle, button, anchors };

    if (k === 'button') {
      data.button = { ...data.button, bgColor: color };
    } else if (k === 'button-color') {
      data.button = { ...data.button, color: color };
    } else {
      // @ts-ignore
      data[k] = { ...data[k], color: color };
    }
    onAdditionalContentChange && onAdditionalContentChange({ ...data });
  }, [title, button, subtitle, onAdditionalContentChange]);

  const onButtonBgChange = useCallback((color: string) => {
    const data = { title, subtitle, button, anchors };

    onAdditionalContentChange && onAdditionalContentChange({
      ...data, button: { ...button, color }
    })
  }, [title, button, subtitle, anchors, onAdditionalContentChange])




  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsAutoFocus(true);
        } else {
          setIsAutoFocus(false);
        }
      },
      { threshold: 0.1 }
    );

    if (inputRef.current) {
      observer.observe(inputRef.current);
    }

    return () => {
      if (inputRef.current) {
        observer.unobserve(inputRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setKey(prevKey => prevKey + 1);
  }, [isAutoFocus]);

  useEffect(() => {
    onTitlePositionChange && onTitlePositionChange(postTextPos);
  }, [postTextPos]);

  const CoverInputs = useMemo(() => (
    <FlexBox direction="column" directionMobile="column" gy={24}>

      <ProjectTitleInput
        type={INPUT_TYPES.counter}
        orientation="column"
        label={t('project_edit.settings_block.labels.title')}
        placeholder={t('project_edit.type_something_new')}
        onChange={onTitleChange}
        value={Lang(title?.text, language)}
        required
        key={key}
      />

      {/* <CommonInput 
              type={INPUT_TYPES.counter}
              label={t('project_edit.settings_block.labels.title')}
              orientation="column"
              placeholder={t('project_edit.type_something_new')}
              maxLength={75}
              value={Lang(title?.text, language)}
              onChange={onTitleChange}
              required
              key={key}
            /> */}

      <CommonInput
        type={INPUT_TYPES.counter}
        placeholder={t('project_edit.type_something_new')}
        label={t('project_edit.settings_block.labels.subtitle')}
        orientation="column"
        maxLength={100}
        value={Lang(subtitle?.text, language)}
        onChange={onSubtitleChange}
      />
    </FlexBox>
  ), [title, subtitle, is743, t, language, onTitleChange, isMobile, onSubtitleChange, isAutoFocus, key])


  const TextPosButtons = useMemo(() => {
    return <FlexBox direction="column">
      <ProjectEditPageGridLabel isPresentation offsetBottom={8} fullWidth style={{ marginRight: is743 ? 40 : 0, paddingTop: 0, whiteSpace: 'nowrap', placeItems: 'center' }}>{t('project_edit.settings_block.labels.position_button')}</ProjectEditPageGridLabel>
      <FlexBox gx={24}>
        <AddTextBottom
          active={postTextPos === 'default'}
          onClick={() => setPostTextPos('default')}
          current={titlePosition === 'big-caption'}
        />
        <AddTextTop
          active={postTextPos === 'default-top'}
          onClick={() => setPostTextPos('default-top')}
          current={titlePosition === 'big-caption'}
        />
      </FlexBox>
    </FlexBox>
  }, [is743, postTextPos, t, titlePosition])

  const ButtonInputs = useMemo(() => {

    if (!button?.visible) {
      return <FlexBox offsetTop={24}>{TextPosButtons}</FlexBox>
    }

    return (
      <FlexBox width={'100%'} widthMobile={'100%'} direction="column" directionMobile="column" gy={24} offsetTop={24}>
        <CommonInput
          value={Lang(button?.text, language)}
          type={INPUT_TYPES.counter}
          onChange={(ev: any) => onButtonChange(ev, 'text')}
          placeholder={t('project_edit.type_something_new')}
          label={t('project_edit.settings_block.labels.text_on_button')}
          orientation="column"
          maxLength={35}
        />
        <CommonInput
          value={button.href}
          type={INPUT_TYPES.counter}
          maxLength={100}
          onChange={(ev: any) => onButtonChange(ev, 'href')}
          placeholder={t('project_edit.type_something_new')}
          label={t('project_edit.settings_block.labels.link_on_button')}
          orientation="column"
        />
        {TextPosButtons}
        {!isProDeziiign && <FlexBox direction="column" directionMobile="column">
          <ProjectEditPageGridLabel isPresentation fullWidth isTable isTeamEdit style={{ marginBottom: 8 }}>
            {t(`project_edit.settings_block.labels.button_bg_color`)}
          </ProjectEditPageGridLabel>
          <DropdownWrapper directionMobile="column" offsetBottom={24} width={'100%'} widthMobile={'100%'}>
            <ProjectEditPageGridInput isTeamEdit isColumns>
              <Input.Dropdown
                value={defaultBgColor}
                onChange={(option: any) => {
                  onButtonBgChange(option.value)
                }}
                options={translatedBgColors}
              />
            </ProjectEditPageGridInput>
          </DropdownWrapper>
        </FlexBox>}

      </FlexBox>
    )
  }, [button, is743, translatedBgColors, defaultBgColor, t, language, onButtonChange, onButtonBgChange, postTextPos])

  const VisibleSettings = useMemo(() => (
    <FlexBox
      style={{ display: is743 ? 'none' : 'flex' }}
      direction='column'
      directionMobile="column"
      offsetTop={24}
      offsetTopMobile={24}
      gy={24}
    >
      <BilingWrapper isPresentation>
        <Checkbox
          label={t('project_edit.hide_title')}
          checked={!title?.visible}
          dark={!isGeograffee}
          onClick={() => {
            onToggleButtonVisible('title');
            onToggleButtonVisible('subtitle')
          }}
        />
      </BilingWrapper>
      {isProDeziiign && <BilingWrapper isPresentation>
        <Checkbox
          label={t('project_edit.hide_project_info')}
          checked={!!projectInfoIsHidden}
          dark={!isGeograffee}
          onClick={() => {
            toggleHideInfo?.((prev: boolean) => !prev);
          }}
        />
      </BilingWrapper>}
      <BilingWrapper>
        <Checkbox
          label={t('project_edit.settings_block.labels.button')}
          checked={button?.visible}
          dark={!isGeograffee}
          onClick={() => onToggleButtonVisible('button')}
        />
      </BilingWrapper>
    </FlexBox>
  ), [button, isGeograffee, projectInfoIsHidden, title, t, onToggleButtonVisible, is743])

  const LocalPickers = useMemo(() => {
    return (
      <LocalPickerWrapper direction="column" directionMobile="column">
        {VisibleSettings}
        {ButtonInputs}
        <ColorButtonsWrapper isProDeziiign={isProDeziiign} offsetTop={24}>
          {/*@ts-ignore*/}
          {Object.entries(additionalContent)?.filter(([k, v]) => k !== 'anchors')?.map(([k, v]) => {
            //@ts-ignore
            if (k === 'anchors' || (k === 'button' && !v?.visible)) {
              return false;
            }
            if (isProDeziiign) {
              return (
                <React.Fragment key={k}>
                  <ColorPicker
                    isShort
                    //@ts-ignore 
                    value={k === 'button' ? v?.bgColor : v?.color}
                    onChange={(val: string) => onLocalColorChange(val, k)}
                    label={t(`project_edit.settings_block.picker_labels.${k}`)}
                    isProDeziiign={isProDeziiign}
                  />
                  {k === 'button' && (
                    <ColorPicker
                      isShort
                      isButtonColor
                      value={v?.color}
                      onChange={(val: string) => onLocalColorChange(val, `${k}-color`)}
                      label={t(`project_edit.settings_block.labels.button_bg_color`)}
                      isProDeziiign={isProDeziiign}
                    />
                  )}
                </React.Fragment>

              )
            }
            return (
              <ColorPicker
                isShort
                //@ts-ignore 
                value={k === 'button' ? v?.bgColor : v?.color}
                onChange={(val: string) => onLocalColorChange(val, k)}
                label={t(`project_edit.settings_block.picker_labels.${k}`)}
              />
            )
          }
          )}
        </ColorButtonsWrapper>
      </LocalPickerWrapper>
    )
  }, [additionalContent, VisibleSettings, ButtonInputs, t, onLocalColorChange])

  const ErrorsBlock = useMemo(() => {

    if (errorsArray) {
      return (
        <FlexBox offsetTop={is743 ? 0 : 24} offsetTopMobile={24} style={{ order: is743 ? 1 : 0 }}>
          <ErrorsContainer>
            {(errorProjectFill || longReadLength === 0) && (
              <FlexBox direction="column" directionMobile='column' offsetTop={is743 ? 15 : 24} offsetTopMobile={is743 ? 0 : 15} offsetRight={32} offsetRightMobile={is743 ? 0 : 32}>
                <ErrorItem
                  color={theme.projectEdit.textTips}
                  direction="column"
                  directionMobile="column"
                >
                  <ul>
                    {errorsArray.slice(0, -1).map((error) => (
                      <li>
                        <FlexBox direction='row'>
                          <span style={{ marginRight: 8 }}>{`—`}</span>
                          <span>{`${error}`}</span>
                        </FlexBox>
                      </li>
                    ))}
                  </ul>
                </ErrorItem>
                {!!errorsArray?.length && (
                  <FlexBox direction='row' directionMobile='row' offsetTop={is743 ? 0 : 16} offsetTopMobile={is743 ? 0 : 16}>
                    <LabelRedStar style={{ marginLeft: 0 }}>*</LabelRedStar>
                    <RequiredErrorMsg>{errorsArray[errorsArray.length - 1]}</RequiredErrorMsg>
                  </FlexBox>
                )}
              </FlexBox>
            )}
            <div />
          </ErrorsContainer>
        </FlexBox>
      )
    }
  }, [errorsArray, errorProjectFill, longReadLength, is743, theme])

  return (
    <Left vtype={vTYPE}>
      <LeftContent>
        <SettingsLabel>{t('project_edit.cover_title')}</SettingsLabel>
        {CoverInputs}
        {LocalPickers}
        {ErrorsBlock}
      </LeftContent>
    </Left>
  )
}import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AddTextBottom,
  AddTextTop,
  ColorButtonsWrapper,
  CountLength,
  ErrorsContainer,
  Left,
  LeftContent,
  LocalPickerWrapper,
  SettingsLabel
} from "../styles";
import { CommonInput, FlexBox, INPUT_TYPES, useBreakpoint, useIsMobile } from "@brg/ui";
import { ProjectEditPageGridLabel } from "shared/pages/projectEdit/styles";
import { ProjectEditPageGridInput } from "_deziiign/books/pages/BookEdit/styles";
import { Input } from "@brg/input";
import { LayoutConst } from "core";
import { useTranslation } from "react-i18next";
import { ProjectAuthorsChangeCbT } from "shared/pages/projectEdit/ProjectAuthorsEdit";
import { AuthorT } from "shared/pages/projectEdit/types";
import ColorPicker from "../../ColorPicker";
import { BilingWrapper, ErrorItem, RequiredErrorMsg } from "shared/pages/projectEdit/styles";
import { setProjectTitleByLang } from "shared/pages/projectEdit/helpers";
import { AppTester } from "supporting";
import { Checkbox, DropdownWrapper } from "../PresentatitonBlock";
import { LabelRedStar } from "shared/pages/designerEditView/styles";
import { useTheme } from "styled-components";
import { IAddProjectCoverContent } from "../types/AdditionalContentType";
import { Lang } from "@brg/api";
import ProjectTitleInput from "shared/pages/projectEdit/fields/ProjectTitleInput";

const BG_COLORS = [
  { label: 'black', value: '#000000' },
  { label: 'white', value: '#fff' }
]

interface ICoverProps {
  vTYPE: string;
  errorsArray?: string[];
  coAuthors?: AuthorT[];
  titleIsHidden?: boolean;
  projectInfoIsHidden?: boolean;
  additionalContent: IAddProjectCoverContent;
  errorProjectFill?: boolean;
  isBilingual?: boolean;
  longReadLength?: number;
  toggleHideTitle?: (e: any) => void;
  toggleHideInfo?: (e: any) => void;
  onAdditionalContentChange?: (data: any) => void;
  onTitlePositionChange?: (pos: string) => void;
  titlePosition?: string;
  onCoauthorChange?: (data: ProjectAuthorsChangeCbT) => void;
}

export const CoverSettingsBlock: FC<ICoverProps> = ({
  vTYPE,
  errorsArray = [],
  additionalContent,
  projectInfoIsHidden,
  isBilingual,
  errorProjectFill,
  longReadLength,
  toggleHideInfo,
  onAdditionalContentChange,
  onTitlePositionChange,
  titlePosition,
}) => {
  const { t, i18n: { language } } = useTranslation();
  const isMobile = useIsMobile();
  const is743 = useBreakpoint(LayoutConst.screen.s - 1);
  const isGeograffee = AppTester.isGeograffee();
  const isProDeziiign = AppTester.isProDeziiign();
  const theme = useTheme();
  const inputRef = useRef(null);
  const [isAutoFocus, setIsAutoFocus] = useState(false);
  const [key, setKey] = useState(1);
  const [postTextPos, setPostTextPos] = useState(titlePosition || '')


  const { title, subtitle, button, anchors } = useMemo(
    () => (additionalContent), [additionalContent])

  const translatedBgColors = useMemo(
    () => (BG_COLORS?.map(
      (i) => ({ ...i, label: t(`project_edit.settings_block.labels.${i.label}`) })))
    , [t])

  const defaultBgColor = useMemo(
    () => (translatedBgColors?.find((i) => i?.value === button?.color))
    , [button?.color, translatedBgColors])


  const onTitleChange = useCallback((ev: any) => {
    const data = {
      title: {
        ...title, text:
          setProjectTitleByLang(
            isBilingual || false,
            title.text,
            language,
            ev?.target?.value
          )
      },
      subtitle,
      button,
      anchors
    };

    onAdditionalContentChange && onAdditionalContentChange(data)
  }, [title, subtitle, button, language, anchors, onAdditionalContentChange, isBilingual])

  const onSubtitleChange = useCallback((ev: any) => {
    const data = {
      title,
      subtitle: {
        ...subtitle, text:
          setProjectTitleByLang(
            isBilingual || false,
            subtitle.text,
            language,
            ev?.target?.value
          )
      },
      button,
      anchors
    };

    onAdditionalContentChange && onAdditionalContentChange(data)
  }, [subtitle, title, button, language, isBilingual, onAdditionalContentChange])

  const onButtonChange = useCallback((ev: any, field: string) => {
    const isHref = field === 'href';

    const data = {
      title,
      subtitle,
      button: {
        ...button, [field]: isHref ? ev?.target?.value :
          setProjectTitleByLang(
            isBilingual || false,
            // @ts-ignore
            button?.[field],
            language,
            ev?.target?.value
          )
      },
      anchors
    };

    onAdditionalContentChange && onAdditionalContentChange(data)
  }, [button, title, subtitle, onAdditionalContentChange, language, isBilingual])

  const onToggleButtonVisible = useCallback((field: string) => {
    const data = { title, subtitle, button };

    if (['title', 'subtitle']?.includes(field)) {
      onAdditionalContentChange && onAdditionalContentChange({
        title: { ...title, visible: !title?.visible },
        subtitle: { ...subtitle, visible: !subtitle?.visible },
        button,
        anchors
      })
      return;
    }

    onAdditionalContentChange && onAdditionalContentChange({
      ...data,
      [field]: {
        // @ts-ignore
        ...data[field as keyof IAddProjectCoverContent]
        // @ts-ignore
        , visible: !data[field as keyof IAddProjectCoverContent].visible
      }
    })
  }, [title, button, subtitle, anchors, onAdditionalContentChange])

  const onLocalColorChange = useCallback((color: string, k: any) => {
    const data = { title, subtitle, button, anchors };

    if (k === 'button') {
      data.button = { ...data.button, bgColor: color };
    } else if (k === 'button-color') {
      data.button = { ...data.button, color: color };
    } else {
      // @ts-ignore
      data[k] = { ...data[k], color: color };
    }
    onAdditionalContentChange && onAdditionalContentChange({ ...data });
  }, [title, button, subtitle, onAdditionalContentChange]);

  const onButtonBgChange = useCallback((color: string) => {
    const data = { title, subtitle, button, anchors };

    onAdditionalContentChange && onAdditionalContentChange({
      ...data, button: { ...button, color }
    })
  }, [title, button, subtitle, anchors, onAdditionalContentChange])




  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsAutoFocus(true);
        } else {
          setIsAutoFocus(false);
        }
      },
      { threshold: 0.1 }
    );

    if (inputRef.current) {
      observer.observe(inputRef.current);
    }

    return () => {
      if (inputRef.current) {
        observer.unobserve(inputRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setKey(prevKey => prevKey + 1);
  }, [isAutoFocus]);

  useEffect(() => {
    onTitlePositionChange && onTitlePositionChange(postTextPos);
  }, [postTextPos]);

  const CoverInputs = useMemo(() => (
    <FlexBox direction="column" directionMobile="column" gy={24}>

      <ProjectTitleInput
        type={INPUT_TYPES.counter}
        orientation="column"
        label={t('project_edit.settings_block.labels.title')}
        placeholder={t('project_edit.type_something_new')}
        onChange={onTitleChange}
        value={Lang(title?.text, language)}
        required
        key={key}
      />

      {/* <CommonInput 
              type={INPUT_TYPES.counter}
              label={t('project_edit.settings_block.labels.title')}
              orientation="column"
              placeholder={t('project_edit.type_something_new')}
              maxLength={75}
              value={Lang(title?.text, language)}
              onChange={onTitleChange}
              required
              key={key}
            /> */}

      <CommonInput
        type={INPUT_TYPES.counter}
        placeholder={t('project_edit.type_something_new')}
        label={t('project_edit.settings_block.labels.subtitle')}
        orientation="column"
        maxLength={100}
        value={Lang(subtitle?.text, language)}
        onChange={onSubtitleChange}
      />
    </FlexBox>
  ), [title, subtitle, is743, t, language, onTitleChange, isMobile, onSubtitleChange, isAutoFocus, key])


  const TextPosButtons = useMemo(() => {
    return <FlexBox direction="column">
      <ProjectEditPageGridLabel isPresentation offsetBottom={8} fullWidth style={{ marginRight: is743 ? 40 : 0, paddingTop: 0, whiteSpace: 'nowrap', placeItems: 'center' }}>{t('project_edit.settings_block.labels.position_button')}</ProjectEditPageGridLabel>
      <FlexBox gx={24}>
        <AddTextBottom
          active={postTextPos === 'default'}
          onClick={() => setPostTextPos('default')}
          current={titlePosition === 'big-caption'}
        />
        <AddTextTop
          active={postTextPos === 'default-top'}
          onClick={() => setPostTextPos('default-top')}
          current={titlePosition === 'big-caption'}
        />
      </FlexBox>
    </FlexBox>
  }, [is743, postTextPos, t, titlePosition])

  const ButtonInputs = useMemo(() => {

    if (!button?.visible) {
      return <FlexBox offsetTop={24}>{TextPosButtons}</FlexBox>
    }

    return (
      <FlexBox width={'100%'} widthMobile={'100%'} direction="column" directionMobile="column" gy={24} offsetTop={24}>
        <CommonInput
          value={Lang(button?.text, language)}
          type={INPUT_TYPES.counter}
          onChange={(ev: any) => onButtonChange(ev, 'text')}
          placeholder={t('project_edit.type_something_new')}
          label={t('project_edit.settings_block.labels.text_on_button')}
          orientation="column"
          maxLength={35}
        />
        <CommonInput
          value={button.href}
          type={INPUT_TYPES.counter}
          maxLength={100}
          onChange={(ev: any) => onButtonChange(ev, 'href')}
          placeholder={t('project_edit.type_something_new')}
          label={t('project_edit.settings_block.labels.link_on_button')}
          orientation="column"
        />
        {TextPosButtons}
        {!isProDeziiign && <FlexBox direction="column" directionMobile="column">
          <ProjectEditPageGridLabel isPresentation fullWidth isTable isTeamEdit style={{ marginBottom: 8 }}>
            {t(`project_edit.settings_block.labels.button_bg_color`)}
          </ProjectEditPageGridLabel>
          <DropdownWrapper directionMobile="column" offsetBottom={24} width={'100%'} widthMobile={'100%'}>
            <ProjectEditPageGridInput isTeamEdit isColumns>
              <Input.Dropdown
                value={defaultBgColor}
                onChange={(option: any) => {
                  onButtonBgChange(option.value)
                }}
                options={translatedBgColors}
              />
            </ProjectEditPageGridInput>
          </DropdownWrapper>
        </FlexBox>}

      </FlexBox>
    )
  }, [button, is743, translatedBgColors, defaultBgColor, t, language, onButtonChange, onButtonBgChange, postTextPos])

  const VisibleSettings = useMemo(() => (
    <FlexBox
      style={{ display: is743 ? 'none' : 'flex' }}
      direction='column'
      directionMobile="column"
      offsetTop={24}
      offsetTopMobile={24}
      gy={24}
    >
      <BilingWrapper isPresentation>
        <Checkbox
          label={t('project_edit.hide_title')}
          checked={!title?.visible}
          dark={!isGeograffee}
          onClick={() => {
            onToggleButtonVisible('title');
            onToggleButtonVisible('subtitle')
          }}
        />
      </BilingWrapper>
      {isProDeziiign && <BilingWrapper isPresentation>
        <Checkbox
          label={t('project_edit.hide_project_info')}
          checked={!!projectInfoIsHidden}
          dark={!isGeograffee}
          onClick={() => {
            toggleHideInfo?.((prev: boolean) => !prev);
          }}
        />
      </BilingWrapper>}
      <BilingWrapper>
        <Checkbox
          label={t('project_edit.settings_block.labels.button')}
          checked={button?.visible}
          dark={!isGeograffee}
          onClick={() => onToggleButtonVisible('button')}
        />
      </BilingWrapper>
    </FlexBox>
  ), [button, isGeograffee, projectInfoIsHidden, title, t, onToggleButtonVisible, is743])

  const LocalPickers = useMemo(() => {
    return (
      <LocalPickerWrapper direction="column" directionMobile="column">
        {VisibleSettings}
        {ButtonInputs}
        <ColorButtonsWrapper isProDeziiign={isProDeziiign} offsetTop={24}>
          {/*@ts-ignore*/}
          {Object.entries(additionalContent)?.filter(([k, v]) => k !== 'anchors')?.map(([k, v]) => {
            //@ts-ignore
            if (k === 'anchors' || (k === 'button' && !v?.visible)) {
              return false;
            }
            if (isProDeziiign) {
              return (
                <React.Fragment key={k}>
                  <ColorPicker
                    isShort
                    //@ts-ignore 
                    value={k === 'button' ? v?.bgColor : v?.color}
                    onChange={(val: string) => onLocalColorChange(val, k)}
                    label={t(`project_edit.settings_block.picker_labels.${k}`)}
                    isProDeziiign={isProDeziiign}
                  />
                  {k === 'button' && (
                    <ColorPicker
                      isShort
                      isButtonColor
                      value={v?.color}
                      onChange={(val: string) => onLocalColorChange(val, `${k}-color`)}
                      label={t(`project_edit.settings_block.labels.button_bg_color`)}
                      isProDeziiign={isProDeziiign}
                    />
                  )}
                </React.Fragment>

              )
            }
            return (
              <ColorPicker
                isShort
                //@ts-ignore 
                value={k === 'button' ? v?.bgColor : v?.color}
                onChange={(val: string) => onLocalColorChange(val, k)}
                label={t(`project_edit.settings_block.picker_labels.${k}`)}
              />
            )
          }
          )}
        </ColorButtonsWrapper>
      </LocalPickerWrapper>
    )
  }, [additionalContent, VisibleSettings, ButtonInputs, t, onLocalColorChange])

  const ErrorsBlock = useMemo(() => {

    if (errorsArray) {
      return (
        <FlexBox offsetTop={is743 ? 0 : 24} offsetTopMobile={24} style={{ order: is743 ? 1 : 0 }}>
          <ErrorsContainer>
            {(errorProjectFill || longReadLength === 0) && (
              <FlexBox direction="column" directionMobile='column' offsetTop={is743 ? 15 : 24} offsetTopMobile={is743 ? 0 : 15} offsetRight={32} offsetRightMobile={is743 ? 0 : 32}>
                <ErrorItem
                  color={theme.projectEdit.textTips}
                  direction="column"
                  directionMobile="column"
                >
                  <ul>
                    {errorsArray.slice(0, -1).map((error) => (
                      <li>
                        <FlexBox direction='row'>
                          <span style={{ marginRight: 8 }}>{`—`}</span>
                          <span>{`${error}`}</span>
                        </FlexBox>
                      </li>
                    ))}
                  </ul>
                </ErrorItem>
                {!!errorsArray?.length && (
                  <FlexBox direction='row' directionMobile='row' offsetTop={is743 ? 0 : 16} offsetTopMobile={is743 ? 0 : 16}>
                    <LabelRedStar style={{ marginLeft: 0 }}>*</LabelRedStar>
                    <RequiredErrorMsg>{errorsArray[errorsArray.length - 1]}</RequiredErrorMsg>
                  </FlexBox>
                )}
              </FlexBox>
            )}
            <div />
          </ErrorsContainer>
        </FlexBox>
      )
    }
  }, [errorsArray, errorProjectFill, longReadLength, is743, theme])

  return (
    <Left vtype={vTYPE}>
      <LeftContent>
        <SettingsLabel>{t('project_edit.cover_title')}</SettingsLabel>
        {CoverInputs}
        {LocalPickers}
        {ErrorsBlock}
      </LeftContent>
    </Left>
  )
}